import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Anchor } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const fmt = (v) => `£${Number(v||0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * "Set Current Balance" override — bypasses the full edit dialog and
 * goes straight to the authoritative anchor. Backend re-baselines
 * `total_balance` so the Payment View chain ends exactly on the typed
 * value in the current month. No other field is touched.
 */
export default function SetCurrentBalanceDialog({ open, onClose, debt, api, onSaved }) {
  const [value, setValue]   = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && debt) setValue(String(debt.current_balance ?? ""));
  }, [open, debt]);

  const typed = Number(value);
  const valid = !Number.isNaN(typed) && typed >= 0 && value !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${api}/debts/${debt.id}/set-current-balance`, {
        current_balance: typed,
      });
      toast.success(r.data?.message || `CURRENT BAL set to ${fmt(typed)}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set current balance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm rounded-sm border border-gray-200" data-testid="set-current-balance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-base font-semibold tracking-tight">
            <Anchor className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
            Set CURRENT BAL — {debt?.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 pt-1 leading-snug">
            Use this when you've reconciled the balance from your bank statement and want to anchor the system to it. TOTAL BALANCE is silently re-baselined so the Payment View chain ends exactly on the typed value this month.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Current Balance (£) *</Label>
            <Input
              type="number" step="0.01" min="0" required autoFocus
              data-testid="set-current-balance-input"
              value={value} onChange={(e) => setValue(e.target.value)}
              className="rounded-sm mt-1 font-mono-data text-lg"
              placeholder="e.g. 3250.00"
            />
            <p className="text-[10px] text-gray-500 mt-1.5 leading-snug">
              Current value: <span className="font-mono-data text-gray-700">{fmt(debt?.current_balance)}</span>
            </p>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="set-current-balance-cancel" disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || !valid} data-testid="set-current-balance-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? "Saving…" : "Anchor Balance"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
